import { useState, useEffect, useCallback } from 'react';

export const useSoundManager = () => {
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [audioContext, setAudioContext] = useState(null);
  const [waterDropBuffer, setWaterDropBuffer] = useState(null);

  // Create a fallback water droplet sound using Web Audio API
  const createFallbackWaterDropSound = useCallback(() => {
    try {
      const context = new (window.AudioContext || window.webkitAudioContext)();
      setAudioContext(context);

      // Create a more realistic water droplet sound
      const buffer = context.createBuffer(1, context.sampleRate * 1.0, context.sampleRate);
      const data = buffer.getChannelData(0);
      
      for (let i = 0; i < buffer.length; i++) {
        const t = i / context.sampleRate;
        
        // Create a complex water droplet sound with multiple frequencies
        const dropFreq = 1200; // Main drop frequency
        const splashFreq = 800; // Splash frequency
        const decay = Math.exp(-t * 6); // Faster decay for water
        
        // Combine multiple sine waves for realism
        const dropTone = Math.sin(2 * Math.PI * dropFreq * t) * decay;
        const splashTone = Math.sin(2 * Math.PI * splashFreq * t) * decay * 0.5;
        const highTone = Math.sin(2 * Math.PI * 2000 * t) * decay * 0.3;
        
        // Add some randomness for natural sound
        const noise = (Math.random() - 0.5) * 0.1 * decay;
        
        data[i] = (dropTone + splashTone + highTone + noise) * 0.4;
      }
      
      setWaterDropBuffer(buffer);
    } catch (error) {
      console.warn('Fallback audio creation failed:', error);
    }
  }, []);

  // Initialize audio context and load sounds
  useEffect(() => {
    const initAudio = async () => {
      try {
        // Create audio context
        const context = new (window.AudioContext || window.webkitAudioContext)();
        setAudioContext(context);

        // Load water droplet sound
        const response = await fetch('/sounds/water-drop.mp3');
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await context.decodeAudioData(arrayBuffer);
        setWaterDropBuffer(audioBuffer);
      } catch (error) {
        console.warn('Audio not available:', error);
        // Fallback: create a simple water droplet sound using Web Audio API
        createFallbackWaterDropSound();
      }
    };

    // Initialize audio immediately
    initAudio();

    return () => {
      // Use a ref to avoid dependency issues
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [createFallbackWaterDropSound]); // eslint-disable-line react-hooks/exhaustive-deps

  // Play water droplet sound
  const playWaterDropSound = useCallback(() => {
    if (!isSoundEnabled || !audioContext || !waterDropBuffer) return;

    try {
      // Resume audio context if it's suspended (browser requirement)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const source = audioContext.createBufferSource();
      source.buffer = waterDropBuffer;
      
      // Create a gain node for volume control
      const gainNode = audioContext.createGain();
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // 30% volume
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      source.start(0);
    } catch (error) {
      console.warn('Failed to play water drop sound:', error);
    }
  }, [isSoundEnabled, waterDropBuffer, audioContext]);

  // Toggle sound on/off
  const toggleSound = useCallback(() => {
    setIsSoundEnabled(prev => !prev);
  }, []);

  // Ensure audio context is ready
  const ensureAudioReady = useCallback(async () => {
    if (audioContext && audioContext.state === 'suspended') {
      try {
        await audioContext.resume();
        console.log('Audio context resumed successfully');
      } catch (error) {
        console.warn('Failed to resume audio context:', error);
      }
    }
  }, [audioContext]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isSoundEnabled,
    toggleSound,
    playWaterDropSound,
    ensureAudioReady
  };
};
